/**
 * @fileoverview Student-portal-specific "Studio Quiet" primitives shared across
 * the five student screens: SHeader (page header with a serif display title),
 * SEmpty (centered empty state) and SToggle (notification switch). Built on the
 * shared studio token layer + primitives. Port of the design handoff's SHeader,
 * SEmpty and SToggle.
 */

import { cn } from "@/lib/utils";
import { Icon, Button } from "@/components/studio/primitives";

/**
 * Page header: micro eyebrow + serif display title + optional subtitle, with an
 * optional right-aligned action node. Baseline-aligned, generous bottom margin.
 */
export function SHeader({ eyebrow, title, subtitle, action, className }) {
  return (
    <div className={cn("mb-[34px] flex items-end justify-between gap-6", className)}>
      <div className="min-w-0">
        {eyebrow && (
          <div className="font-sans text-[11px] font-semibold uppercase leading-none tracking-[0.06em] text-[var(--text-tertiary)]">
            {eyebrow}
          </div>
        )}
        <h1 className="mt-2 font-serif text-[34px] font-normal leading-[1.05] tracking-[-0.02em] text-[var(--text-primary)] md:text-[44px] md:leading-none">
          {title}
        </h1>
        {subtitle && (
          <p className="mt-3 font-sans text-base text-[var(--text-tertiary)]">{subtitle}</p>
        )}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

/**
 * Centered empty state: soft icon tile + serif headline + body copy + optional
 * large CTA button.
 */
export function SEmpty({ icon, title, body, cta, ctaIcon = "mic", onCta, className }) {
  return (
    <div className={cn("flex flex-col items-center px-8 py-[72px] text-center", className)}>
      <div className="mb-[22px] flex h-[76px] w-[76px] items-center justify-center rounded-[20px] border border-[var(--studio-divider)] bg-[var(--studio-interactive)]">
        <Icon name={icon} size={30} strokeWidth={1.6} className="text-[var(--text-tertiary)]" />
      </div>
      <div className="font-serif text-[26px] font-normal tracking-[-0.01em] text-[var(--text-primary)]">{title}</div>
      {body && (
        <p className="mt-2.5 max-w-[420px] font-sans text-base leading-relaxed text-[var(--text-tertiary)]">{body}</p>
      )}
      {cta && (
        <div className="mt-[26px]">
          <Button size="lg" icon={ctaIcon} onPress={onCta}>{cta}</Button>
        </div>
      )}
    </div>
  );
}

/**
 * Notification-style switch. 46×27 pill track; ON = yellow track + inverse knob
 * to the right, OFF = interactive track + strong-divider border + secondary knob
 * to the left.
 */
export function SToggle({ on, onToggle, ariaLabel }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={ariaLabel}
      onClick={onToggle}
      className={cn(
        "flex h-[27px] w-[46px] shrink-0 items-center rounded-full p-[3px] transition-colors duration-150",
        on
          ? "justify-end border border-transparent bg-[var(--accent-yellow)]"
          : "justify-start border border-[var(--studio-divider-strong)] bg-[var(--studio-interactive)]",
      )}
    >
      <span
        className={cn(
          "h-[21px] w-[21px] rounded-full transition-colors duration-150",
          on ? "bg-[var(--text-inverse)]" : "bg-[var(--text-secondary)]",
        )}
      />
    </button>
  );
}
